<template>
  <div class="indicator-widget f-w f-h">
    <!-- Not using v-if cause we use the component to manage range dates -->
    <ChartPeriodSelector
      v-show="displayRangeSelector"
      ref="periodSelector"
      v-model="displayConfig"
      :default-expanded="false"
      :text="$t('message.INDICATORS')"
      :expandable="false"
      :preset-id="presetId"
      :query-prefix="`indicatorWidget${widget.id}`"
      @presetPick="presetId = $event"
      @presetEditorOpened="setMaxZIndex(true)"
      @presetEditorClosed="setMaxZIndex(false)"
    />
    <div
      v-if="!cfgTemplateEdition && windicator"
      class="indicator-widget__indicator-wrapper"
      :class="{ 'indicator-widget__indicator-wrapper--with-range': displayRangeSelector }"
    >
      <Indicator
        :key="chartKey + 1"
        class="f-w f-h"
        :indicator="windicator"
        :visible-chart-range="visibleValuesRange"
        :range="allValuesRange"
        :custom-config="displayConfig"
        :refreshing="refreshing"
        :cumulated="cumulated === 1"
        :team-ids="utilsSelectedTeamIds"
        show-labels
        show-dots
        disabled
        @nameClicked="chartClicked"
        @chartClicked="chartClicked"
        @changePeriod="selectConfigFromUnit"
      />
    </div>
    <div v-else class="f-w f-h h-v-centerer text-center">
      <span v-if="!cfgTemplateEdition">{{ $t('message.WIDGET_INDIC_NO_SELECTED') }}</span>
    </div>
  </div>
</template>

<script>
/**
 * Indicator container that may display a chart or a doughnut
 */
import { isEqual } from 'lodash';
// State management
import { mapState } from 'vuex';
import {
  FETCH_INDICATOR_THEMES,
  FETCH_MANAGED_INDICATORS,
  FETCH_LIBRARY_INDICATORS,
  BUFFER_FETCH_INDICATORS,
  BUFFER_FETCH_CUMULATED_INDICATORS,
  FETCH_INDICATOR_DISPLAY_CONFIGS,
} from '@/store/action-types';
import {
  GET_INDICATOR_THEMES,
  GET_MANAGED_INDICATORS,
  GET_LIBRARY_INDICATORS,
  GET_INDICATOR_DISPLAY_CONFIGS,
} from '@/store/mutation-types';
// Components
import ChartPeriodSelector from '@/components/chart-period-selector.vue';
import Indicator from '@/components/indicator.vue';
// Parent
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import mixinManagedTeamsUtils from '@/mixins/managed-teams-utils.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import { calcPeriodStartDate } from '@/utils/main';
import { mapLibraryToRadio, computeConfigStart } from '@/utils/indicator-utils';


export default {

  name: 'IndicatorWidget',

  components: {
    ChartPeriodSelector,
    Indicator,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinSocketHandler, mixinManagedTeamsUtils],

  props: {
    cumulated: {
      type: Number,
      default: 0,
      validator: v => v === 0 || v === 1,
    },
    indicatorId: {
      type: Number,
      default: 0,
    },
    // WARNING: Used in cfg page assignation
    libraryIndicatorId: {
      type: Number,
      default: 0,
    },
    displayRangeSelector: {
      type: Number,
      default: 0,
    },
    defaultPeriod: {
      type: Number,
      default: 1,
    },
    // TODO: Remove this field in the next versions
    defaultDaysGap: {
      type: Number,
      default: 0,
    },
    teamsFilter: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      chartKey: 1,
      refreshing: false,
      displayConfig: this.$store.getters.defaultIndicDisplayConfig,
    };
  },

  computed: {
    config() {
      const config = [
        {
          key: 'indicTopSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'displayRangeSelector',
          value: this.displayRangeSelector,
          component: 'IntCheckbox',
          options: {
            class: 'mt5em',
            title: this.$t('message.WIDGET_INDIC_DISPLAY_RANGE'),
          },
        },
        {
          key: 'br_before_cumul',
          value: '',
          component: 'Br',
        },
        {
          key: 'cumulated',
          value: this.cumulated,
          component: 'IntCheckbox',
          options: {
            class: 'mt3em',
            title: this.$t('message.WIDGET_INDIC_CUULATED'),
          },
        },
        {
          key: 'defaultPeriod',
          value: this.defaultPeriod,
          component: 'IndicatorDisplayConfigField',
          options: {
            class: 'mt5em',
          },
        },
        // TODO: Remove this field in the next versions
        {
          key: 'defaultDaysGap',
          value: this.defaultDaysGap,
          component: 'InputNumber',
          options: {
            legend: this.$t('message.WIDGET_INDIC_GAP_DAYS') + this.$t('message.TWO_DOTS'),
            class: 'widget-config mt10em',
          },
        },
        {
          key: '',
          value: '',
          component: 'Div',
          options: {
            class: 'field-hint',
            id: `indicator-hint${this.widget.id}`,
          },
        },
      ];
      if (this.cumulated && !this.cfgTemplateEdition) {
        // ALlow filtering teamsfrom which the values are fetched
        config.push({
          key: 'teamsFilter',
          value: this.utilsSelectedTeamIds,
          component: 'AdvancedTeamPicker',
          options: {
            legend: this.$t('message.TEAMS') + this.$t('message.TWO_DOTS'),
            class: 'mt10em',
            displayAllTeams: true,
            disableInaccessible: true,
          },
        });
      }
      // When editing a page template, the user may only choose amongst library indicators
      // Then, upon applying that page to a team, Shizen wil find any indicator with that libraryId for this team
      // and display it
      if (!this.cfgTemplateEdition) {
        config.splice(4, 0, {
          key: 'indicIndicTopSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        });
        config.splice(5, 0, {
          key: 'indicatorId',
          value: this.indicatorId,
          component: 'EditableLabelRadio',
          options: {
            class: 'mt3em',
            legend: this.$t('message.INDICATOR'),
            items: this.cumulated ? this.allLibraryThemes : this.allIndicators,
            labelKey: 'name',
            placeholder: this.$t('message.NONE'),
          },
        });
        config.splice(6, 0, {
          key: 'indicIndicBotSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        });
      } else {
        config.splice(3, 0, {
          key: 'libraryIndicatorId',
          value: this.libraryIndicatorId,
          component: 'EditableLabelRadio',
          options: {
            class: 'mt3em',
            legend: this.$t('message.INDICATOR'),
            items: this.allLibraryThemes,
            labelKey: 'name',
            placeholder: this.$t('message.NONE'),
          },
        });
        config.splice(4, 0, {
          key: 'libraryIndicatorIdHint',
          value: '',
          component: 'Div',
          options: {
            class: 'field-hint',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.CFG_PAGE_LIBRARY_INDIC_HINT'),
          }],
        });
      }
      config.push(this.defaultBgColorConfig);
      return config;
    },
    ...mapState({
      customConfigs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
      customConfigsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
      themeIndicatorsLoading: state => state[GET_INDICATOR_THEMES.loadingKey],
      libraryIndicatorsLoading: state => state[GET_LIBRARY_INDICATORS.loadingKey],
      libraryIndicators: state => state[GET_LIBRARY_INDICATORS.stateKey] || {},
      managedIndicatorsLoading: state => state[GET_MANAGED_INDICATORS.loadingKey],
      managedIndicators: state => state[GET_MANAGED_INDICATORS.stateKey] || [],
      stateIndicators: state => state.indicators,
      cumulatedIndicators: state => state.cumulatedIndicators,
    }),
    /** Array: Array of available custom configuration of this team */
    allCustomConfigs() {
      return [
        ...this.$store.getters.indicDisplayConfigs,
        ...this.customConfigs.map(item => ({
          ...item,
          pointPeriodObj: { period: item.pointPeriod, unit: item.pointUnit, startDay: item.pointStartDay },
          chartPeriodObj: { period: item.chartPeriod, unit: item.chartUnit },
          chartEnd: item.chartEnd || this.$moment(),
        })),
      ];
    },
    /** Array: Indicators to pick from */
    allIndicators() {
      const indicators = [{
        title: this.$t('message.WIDGET_INDICS_OF_CURRENT_TEAM'),
        radioItems: this.$store.getters.themeIndicators || [],
      }];
      this.managedIndicators.forEach((team) => {
        if (team.id !== this.currentTeam.id) {
          const teamIndicators = [];
          team.themes.forEach((theme) => {
            teamIndicators.push(...theme.indicators.map(indic => ({
              id: Number(indic.id),
              name: `${indic.name} - ${theme.name} (${team.name})`,
            })));
          });
          indicators.push({
            title: team.name,
            radioItems: teamIndicators,
          });
        }
      });
      return indicators;
    },
    /** Library indicators to pick from */
    allLibraryThemes() {
      return mapLibraryToRadio(this, this.libraryIndicators);
    },
    /** Indicator object including values */
    windicator() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.refreshing = false;
      const id = this.indicatorId;
      return this.cumulated ? this.cumulatedIndicators[id] : this.stateIndicators[id];
    },
    /** This settings may be a little tricky to understand */
    periodHint() {
      if (this.defaultPeriod === 0) {
        return this.$t('message.WIDGET_INDIC_HINT_ONEDAY', {
          day: this.visibleValuesRange.end.format(this.$t('minDateFormat')),
        });
      }
      return this.$t('message.WIDGET_INDIC_HINT_RANGE', {
        start: this.visibleValuesRange.start.format(this.$t('minDateFormat')),
        end: this.visibleValuesRange.end.format(this.$t('minDateFormat')),
      });
    },
    /** NOTE: Used in mixins/managed-team-utils to get the value of utilsSelectedTeamIds */
    utilsSelectedTeamIdsToFilter() {
      return this.teamsFilter;
    },
    /** Indicator config ID */
    presetId: {
      get() {
        const { presetId } = this.queryParams;
        return presetId === null || isNaN(presetId) ? this.defaultPeriod : Number(presetId);
      },
      set(presetId) {
        // Preset picked from the widget => update query for meeting sync
        this.setQueryParams({ presetId });
      },
    },
    /** Object: Range of chart's visible values */
    visibleValuesRange() {
      if (!this.displayConfig) {
        return null;
      }
      const { chartPeriodObj, pointPeriodObj, chartEnd } = this.displayConfig;
      return {
        start: calcPeriodStartDate(chartEnd, chartPeriodObj, pointPeriodObj),
        end: chartEnd,
      };
    },
    /**
     * Object: Range of all values, including values not displayed.
     * Especially useful when building a cumulated chart (where increasingValues > 0).
     * eg: Values should be cumulated each month from the 1st of each month
     *     => need the values from the 1st day of month
     */
    allValuesRange() {
      if (!this.displayConfig) {
        return null;
      }
      if (!this.displayConfig.increasingValues) {
        // No increasing values, no need for an extended range
        return this.visibleValuesRange;
      }
      const { chartEnd, configStart } = computeConfigStart(this.displayConfig, this.visibleValuesRange.start);
      return { start: this.$moment(configStart), end: chartEnd };
    },
    /** Object: Range to use as an XHR param */
    rangeParam() {
      return {
        start: this.allValuesRange.start.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end: this.allValuesRange.end.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      };
    },
  },

  watch: {
    rangeParam(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.refresh();
      }
    },
    indicatorId() {
      this.refresh();
    },
    displayRangeSelector() {
      this.sizeChanged();
    },
    managedTeams() {
      // Managed teams have been loaded. Before that, teamsFilter was useless
      this.refresh();
    },
    utilsSelectedTeamIds(newVal, oldVal) {
      if (this.cumulated && !isEqual(newVal, oldVal)) {
        this.refresh();
      }
    },
    // defaultPeriod changed, change the display
    defaultPeriod(newVal) {
      this.presetId = newVal;
    },
    // Indicators are done loading, update the list if currently displayed
    themeIndicatorsLoading(isLoading) {
      if (!isLoading) {
        this.$emit('updateConfigOptions');
      }
    },
    managedIndicatorsLoading(isLoading) {
      if (!isLoading) {
        this.$emit('updateConfigOptions');
      }
    },
  },

  created() {
    const config = this.allCustomConfigs.find(cfg => cfg.id === Number(this.defaultPeriod));
    if (config) {
      this.displayConfig = config;
    }
  },

  mounted() {
    if (this.editable && !this.libraryIndicatorsLoading) {
      this.$store.dispatch(FETCH_LIBRARY_INDICATORS);
    }
    // Edition of a template, indicators of teams are not needed
    // No need to refresh either as no indicator will be displayed here
    if (this.cfgTemplateEdition) {
      return;
    }
    if (!this.customConfigsLoading) {
      this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
    }
    if (this.editable && !this.themeIndicatorsLoading) {
      // Allow choosing between team's indicators and managed teams indicators
      this.$store.dispatch(FETCH_INDICATOR_THEMES);
    }
    if (!this.managedIndicatorsLoading && (this.editable || this.cumulated)) {
      this.$store.dispatch(FETCH_MANAGED_INDICATORS, { noValues: true, teams: true });
    }
    this.refresh();
    this.registerSocketHandlers();
  },

  methods: {
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.INDICATORS, (msg) => {
        if (Number(this.windicator.id) !== Number(msg.id)) return;
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PUBLIC_INDICATOR, (msg) => {
        if (Number(this.windicator.copyPublic) !== Number(msg.id)) return;
        this.refresh();
      });
    },
    /** Refresh after a config update or after changing the range */
    refresh() {
      if (this.cfgTemplateEdition) {
        return;
      }
      if (this.indicatorId > 0) {
        this.refreshing = true;
        let action = BUFFER_FETCH_INDICATORS;
        let payload = { values: true, indicatorId: [this.indicatorId] };
        if (this.cumulated) {
          action = BUFFER_FETCH_CUMULATED_INDICATORS;
          payload = {
            libraryIds: [this.indicatorId],
            teams: this.utilsSelectedTeamIds.length ? this.utilsSelectedTeamIds : true,
          };
        }
        this.$nextTick(() => {
          payload.range = this.allValuesRange;
          this.$store.dispatch(action, payload);
        });
      }
    },
    /** Display the period hint on the config panel */
    setPeriodHint() {
      document.getElementById(`indicator-hint${this.widget.id}`).innerHTML = this.periodHint;
    },
    /** Send he user to chart details */
    chartClicked() {
      // Either link to detailed-indicator or it-indicators (if cumulated)
      this.$router.push(this.cumulated
        ? `/it-indicators?detailed=${this.indicatorId}&libraryIds=${this.indicatorId}`
        : `/indicator?detailedIndicatorId=${this.indicatorId}`);
    },
    /** Select a config matching the point period unit */
    selectConfigFromUnit(evt, unit) {
      const preset = this.$store.getters.indicDisplayConfigs.find(config => config.pointPeriodObj.unit === unit);
      if (preset) {
        const config = Object.assign({}, this.displayConfig);
        const { pointPeriodObj, chartPeriodObj } = preset;
        if (!isEqual(pointPeriodObj, config.pointPeriodObj) || !isEqual(chartPeriodObj, config.chartPeriodObj)) {
          config.pointPeriodObj = Object.assign({}, preset.pointPeriodObj);
          config.chartPeriodObj = Object.assign({}, preset.chartPeriodObj);
          this.displayConfig = config;
          this.presetId = preset.id;
        }
      }
    },
    /** Period selector must be updated */
    sizeChanged() {
      if (this.$refs.periodSelector) {
        this.$refs.periodSelector.sizeUpdate();
      }
      this.chartKey++;
    },
    /** Update hint text */
    configEditionStart() {
      this.setPeriodHint();
    },
    /** Working days may have changed. Update */
    userLoaded() {
      this.refresh();
    },
  },
};

</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.indicator-widget.widget-component-child {
  overflow: hidden;

  .chart-period-selector {
    height: 30px;

    .presets-wrapper {
      order: 1;
    }
    .title-range {
      order: 0;
      margin-left: 0;
    }
  }

  .indicator-widget__indicator-wrapper {
    height: 100%;

    &--with-range {
      height: calc(100% - 32px);
    }

    .indicator {
      box-sizing: border-box;

      .indic-name {
        div {
          height: 1.3rem;
        }
      }

      &__main-wrapper {
        height: calc(100% - 1.3rem);
      }
    }
  }
}
.widget-config {
  display: inline-block;

  .input-number {
    width: 3.5rem;
  }
}
</style>
